import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import type { DriverDay } from '~hooks/useDriverDays/models';

import DriverDayStatus from './DriverDayStatus';

const emDash = '-';

export type DriverDayRow = {
  id: string;
  driverDay: DriverDay;
  date: Dayjs;
  driver: string;
  truckNo: string | null;
  vendor: string | null;
  shiftStart: Dayjs | null;
  shiftEnd: Dayjs | null;
  break: number;
  totalHrs: number;
  jobCt: number;
  loadCt: number;
  tickets: number;
  materials: string;
  status: {
    approvable: boolean;
    approved: boolean;
    driverDayId: string;
    driverCompanyId?: string;
    driverId: string;
    driverIsShared?: boolean;
    isOngoingShift: boolean;
    unapprovable: boolean;
  };
};

const formatShiftTime = (value: Dayjs | null) =>
  value ? dayjs.tz(value).format('hh:mm A') : emDash;

export const getDriverDayColumns = (
  onDriverDayChange: (driverDay: DriverDay) => void,
): Array<DataTableColumn<DriverDayRow>> => [
  {
    field: 'date',
    headerName: t('form_fields.date'),
    valueGetter: (row) => row.date.format('MMM D, YY'),
  },
  {
    field: 'driver',
    headerName: t('form_fields.driver'),
    valueGetter: (row) => row.driver,
  },
  {
    field: 'truckNo',
    headerName: t('approvals.driver_day.truck_no'),
    valueGetter: (row) => row.truckNo ?? emDash,
  },
  {
    field: 'vendor',
    headerName: t('form_fields.vendor'),
    valueGetter: (row) => row.vendor ?? emDash,
  },
  {
    field: 'shiftStart',
    headerName: t('approvals.driver_day.shift_start'),
    valueGetter: (row) => formatShiftTime(row.shiftStart),
  },
  {
    field: 'shiftEnd',
    headerName: t('approvals.driver_day.shift_end'),
    valueGetter: (row) => formatShiftTime(row.shiftEnd),
  },
  {
    field: 'break',
    headerName: t('approvals.driver_day.break'),
    valueGetter: (row) => `${row.break} min`,
  },
  {
    field: 'totalHrs',
    headerName: t('approvals.driver_day.total_hrs'),
    valueGetter: (row) => row.totalHrs.toFixed(2),
  },
  {
    field: 'jobCt',
    headerName: t('approvals.driver_day.job_ct'),
    valueGetter: (row) => row.jobCt,
  },
  {
    field: 'loadCt',
    headerName: t('approvals.driver_day.load_ct'),
    valueGetter: (row) => row.loadCt,
  },
  {
    field: 'tickets',
    headerName: t('approvals.driver_day.tickets'),
    valueGetter: (row) => row.tickets,
  },
  {
    field: 'materials',
    headerName: t('administration.material.title'),
    valueGetter: (row) => row.materials,
  },
  {
    field: 'status',
    headerName: t('approvals.driver_day.shift_status'),
    renderCell: (row) => (
      <DriverDayStatus {...row.status} onDriverDayChange={onDriverDayChange} />
    ),
  },
];
